import { Controller } from "@hotwired/stimulus";
import { effect } from "@preact/signals-core";
import { calibrationPayload, cellsAcross, draftGrid, MIN_CELL_PX, regularHeight, regularWidth, switchParity } from "../maps/calibration";
import { cellCounts, MAX_SIDE_PX, sceneSize } from "../maps/grid";
import { type Counts, previewOf, type Resize, type Side, startShare, trimmed } from "../maps/resize";
import { toScreen } from "../maps/viewport";
import type { LiveGrid } from "../types/generated/live";
import type MapBoard from "./map_board_controller";
import { closeOtherPanels } from "./map_panels";

const NUMBERS = new Set(["offset_x", "offset_y", "cell_distance"]);
const MAX_CELLS = 200;

type Plural = { one: string; other: string };
type Labels = { trimmed: string; pulled: Plural; columns: Plural; rows: Plural };

const plural = (label: Plural, count: number) => (Math.abs(count) === 1 ? label.one : label.other);

// Connects to data-controller="map-calibration", beside map-board on the board root.
// The master fits the grid to a printed one: the guide on the canvas and the
// fields here both edit a draft grid in the state's local drafts, which the
// renderer draws; Salvar sends it through the board's outbox, with the map's name,
// which the panel also holds. A blank scene
// keeps its column and row counts, so its size follows the draft grid. Its counts
// grow or shrink the map on the side the master picks, or leave it only what is on
// it (trim): the draft names the cells added on the left and top (`shift`), the
// renderer previews it, and a non-zero shift waits for the server's answer.
export default class extends Controller {
	static targets = [
		"panel",
		"field",
		"parity",
		"across",
		"toggle",
		"cells",
		"save",
		"name",
		"opener",
		"side",
		"trim",
		"trimStatus",
		"legend",
		"legendSize",
		"legendPulled",
		"chip",
	];
	static values = { labels: Object };
	declare readonly panelTarget: HTMLElement;
	declare readonly fieldTargets: (HTMLInputElement | HTMLSelectElement)[];
	declare readonly parityTargets: HTMLElement[];
	declare readonly acrossTarget: HTMLInputElement;
	declare readonly toggleTarget: HTMLElement;
	declare readonly cellsTargets: HTMLInputElement[];
	declare readonly hasAcrossTarget: boolean;
	declare readonly saveTarget: HTMLButtonElement;
	declare readonly nameTarget: HTMLInputElement;
	declare readonly openerTargets: HTMLElement[];
	declare readonly sideTargets: HTMLButtonElement[];
	declare readonly trimTarget: HTMLButtonElement;
	declare readonly trimStatusTarget: HTMLElement;
	declare readonly legendTarget: HTMLElement;
	declare readonly legendSizeTarget: HTMLElement;
	declare readonly legendPulledTarget: HTMLElement;
	declare readonly chipTargets: HTMLElement[];
	declare readonly labelsValue: Labels;
	open = false;
	counts?: Counts;
	// The size when the panel opened, which the drafted counts are measured from.
	saved?: Counts;
	side: { cols: Side; rows: Side } = { cols: "end", rows: "end" };
	// The cells added on the left and top (negative removes): the side rule's, or the trim's.
	shift = { left: 0, top: 0 };
	trimmed = false;
	// The draft grid the trim was worked out on.
	trimGrid?: object;
	opened?: { name: string; visible: boolean; style: string };
	disposeFields?: () => void;

	disconnect() {
		this.close();
	}

	// A map just made from a picture opens here for its grid (?configurar=1), and the address goes back to the map's own.
	ready() {
		const url = new URL(window.location.href);
		if (url.searchParams.get("configurar") !== "1") return;
		url.searchParams.delete("configurar");
		history.replaceState(history.state, "", url);
		this.start();
	}

	toggle() {
		if (this.board?.renderer && !this.open) this.start();
		else this.close();
	}

	start() {
		const { state, renderer } = this.board;
		closeOtherPanels(this.application, this.element, "map-calibration");
		this.open = true;
		const scene = state.view.peek().scene;
		this.counts = this.cellsTargets.length ? cellCounts(scene.grid, scene.width_px, scene.height_px) : undefined;
		const counts = this.counts;
		this.saved = counts && { ...counts };
		this.side = { cols: "end", rows: "end" };
		this.shift = { left: 0, top: 0 };
		this.trimmed = false;
		for (const input of this.cellsTargets) {
			input.value = String(counts[input.dataset.cells]);
			input.removeAttribute("aria-invalid");
		}
		this.saveTarget.disabled = false;
		const draft = draftGrid(scene.grid);
		this.opened = { name: scene.name.trim(), visible: draft.visible, style: draft.style };
		this.nameTarget.value = scene.name;
		this.setDraft(draft);
		renderer.startCalibration((grid) => this.setDraft(grid));
		const disposeFill = effect(() => this.fill(state.local.drafts.value.grid));
		const disposePreview = this.counts ? effect(() => this.paintPreview()) : undefined;
		this.disposeFields = () => {
			disposeFill();
			disposePreview?.();
		};
		this.renderSides();
		this.panelTarget.hidden = false;
		// The panel, not the name field: focusing a text input raises the phone's keyboard.
		this.panelTarget.focus({ preventScroll: true });
		this.toggleTarget.setAttribute("aria-pressed", "true");
		if (this.hasAcrossTarget) this.acrossTarget.value = "";
	}

	close() {
		if (!this.open) return;
		this.open = false;
		const board = this.board;
		// The draft goes first, so the preview's legend and chips hide before their effect is disposed.
		if (board?.state) this.setDraft(undefined);
		this.disposeFields?.();
		board?.renderer?.stopCalibration();
		// Read before hiding: a hidden element gives its focus up at once.
		const inside = this.panelTarget.contains(document.activeElement);
		this.panelTarget.hidden = true;
		this.toggleTarget.setAttribute("aria-pressed", "false");
		// Last first: below sm the board name is only sr-only, still laid out, so Mais ações (after it) has to win.
		if (inside) this.openerTargets.findLast((button) => button.offsetParent)?.focus();
	}

	save() {
		if (this.saveTarget.disabled) return;
		const grid = calibrationPayload(this.draft);
		// Like the name, only what was changed since the panel opened, so a save never reverts another master's.
		for (const key of ["visible", "style"] as const) if (grid[key] === this.opened?.[key]) delete grid[key];
		const { state, outbox, snapshotUrlValue } = this.board;
		const { id } = state.view.peek().scene;
		const { counts } = this;
		// A map from a picture has no counts: its size is the picture's.
		const size = counts ? sceneSize(this.draft, counts) : undefined;
		const { left, top } = this.shift ?? { left: 0, top: 0 };
		const shifted = left !== 0 || top !== 0;
		const name = this.nameTarget.value.trim();
		const extra: { name?: string } = {};
		if (name && name !== this.opened?.name) extra.name = name;
		this.close();
		// A shift moves every piece, drawing and painted cell: the answer carries those ops, so nothing is guessed here.
		outbox.enqueue({
			method: "PATCH",
			url: snapshotUrlValue,
			body: { map_scene: { grid, ...counts, ...(shifted && { left, top }), ...extra } },
			ops: shifted ? [] : [{ op: "update", collection: "scene", id, attrs: { grid: { ...state.view.peek().scene.grid, ...grid }, ...size, ...extra } }],
		});
	}

	// The board's G key shows or hides the grid while the panel is open: the draft and the switch follow, and the new value
	// is what the panel compares with from now on.
	gridShown(visible: boolean) {
		if (!this.open || !this.opened) return;
		this.opened.visible = visible;
		this.setDraft({ ...this.draft, visible });
	}

	// A count typed goes back to the side rule, which a trim had replaced.
	resize() {
		if (this.trimmed) {
			this.trimmed = false;
			this.renderSides();
		}
		this.check();
	}

	// Picks the side of the columns or the rows that a change of count grows or shrinks.
	pick({ params }: { params: { axis: "cols" | "rows"; side: Side } }) {
		this.side[params.axis] = params.side;
		this.trimmed = false;
		this.check();
		this.renderSides();
	}

	// Fills the counts with the smallest map that still holds everything on it, and the sides that cut the rest.
	trim() {
		const grid = this.draft;
		const box = this.trimBox(grid);
		if (!box || !this.counts) return;
		this.applyTrim(box, grid);
		this.check();
		this.renderSides();
	}

	applyTrim({ cols, rows, left, top }: Counts & { left: number; top: number }, grid: object) {
		for (const input of this.cellsTargets) input.value = String(input.dataset.cells === "cols" ? cols : rows);
		this.trimmed = true;
		this.trimGrid = grid;
		this.shift = { left, top };
		this.trimStatusTarget.textContent = this.labelsValue.trimmed.replace("%{size}", `${cols} × ${rows}`);
	}

	// A calibration change after a trim moves what the map holds, so the trim is worked out again on the grid the server will apply.
	retrim(grid: object) {
		const box = this.holds(grid);
		if (box) {
			this.applyTrim(box, grid);
			this.counts = { cols: box.cols, rows: box.rows };
		} else this.trimmed = false;
	}

	// What the saved map holds under `grid` (the draft's: Salvar applies it first), or null: also null when it is already that size.
	trimBox(grid: object) {
		const box = this.holds(grid);
		const saved = this.saved;
		return box && saved && (box.cols !== saved.cols || box.rows !== saved.rows) ? box : null;
	}

	// The smallest frame that holds what is on the saved map under `grid`, which may be the whole map; null when it holds nothing.
	holds(grid: object) {
		const { scene, tokens, drawings, notes } = this.board.state.view.peek();
		const saved = this.saved;
		if (!saved) return null;
		return trimmed(grid as typeof scene.grid, saved, {
			terrain: scene.terrain,
			edges: scene.edges,
			fog: scene.fog,
			tokens: Object.values(tokens ?? {}),
			drawings: Object.values(drawings ?? {}),
			notes: Object.values(notes ?? {}),
		});
	}

	// The side buttons show the side rule, none of them after a trim; the trim button, whether there is anything to cut.
	renderSides() {
		if (!this.counts) return;
		for (const button of this.sideTargets) {
			const { axis, side } = button.dataset;
			button.setAttribute("aria-pressed", String(!this.trimmed && this.side[axis] === side));
		}
		this.trimStatusTarget.hidden = !this.trimmed;
		this.trimTarget.disabled = !this.trimBox(this.draft);
	}

	// A count out of range, or one whose pixel size the server would refuse,
	// holds Salvar, so the counts saved are the ones shown.
	check() {
		const counts = {} as Counts;
		for (const input of this.cellsTargets) {
			const count = input.value.trim() === "" ? Number.NaN : Number(input.value);
			if (Number.isInteger(count) && count >= 1 && count <= MAX_CELLS) {
				counts[input.dataset.cells] = count;
				input.removeAttribute("aria-invalid");
			} else input.setAttribute("aria-invalid", "true");
		}
		const inRange = Object.keys(counts).length === this.cellsTargets.length;
		const oversized = inRange && Object.values(sceneSize(this.draft, counts)).some((px) => px > MAX_SIDE_PX);
		if (oversized) for (const input of this.cellsTargets) input.setAttribute("aria-invalid", "true");
		this.saveTarget.disabled = !inRange || oversized;
		if (this.saveTarget.disabled) return;
		const from = this.saved ?? this.counts;
		this.counts = counts;
		if (!this.trimmed)
			this.shift = { left: startShare(counts.cols - from.cols, this.side?.cols ?? "end"), top: startShare(counts.rows - from.rows, this.side?.rows ?? "end") };
		this.setDraft(this.draft);
	}

	// Re-checks the drafted size, since a cell or offset change can push
	// a blank scene's counts past the server's pixel limit just as resize() does.
	change({ target }: Event & { target: HTMLInputElement | HTMLSelectElement }) {
		const field = target.dataset.field;
		if (field === "hex_parity" && this.counts) this.setDraft(switchParity(this.draft, target.value));
		else if (field === "cell_w" || field === "cell_h") this.changeCell(field, Number.parseFloat(target.value));
		else if (field === "visible") this.setDraft({ ...this.draft, visible: (target as HTMLInputElement).checked });
		else if (!NUMBERS.has(field)) this.setDraft({ ...this.draft, [field]: target.value });
		else {
			const value = Number.parseFloat(target.value);
			const minimum = field === "cell_distance" ? Number.MIN_VALUE : -Infinity;
			if (Number.isFinite(value) && value >= minimum) this.setDraft({ ...this.draft, [field]: value });
		}
		if (this.counts) this.check();
	}

	// The other side follows, so the cell keeps its kind's regular shape.
	changeCell(field: "cell_w" | "cell_h", value: number) {
		const { kind } = this.draft as { kind: string };
		const cell = field === "cell_w" ? { cell_w: value, cell_h: regularHeight(kind, value) } : { cell_w: regularWidth(kind, value), cell_h: value };
		if (Number.isFinite(value) && Math.min(cell.cell_w, cell.cell_h) >= MIN_CELL_PX) this.setDraft({ ...this.draft, ...cell });
	}

	fitAcross() {
		const count = Number.parseInt(this.acrossTarget.value, 10);
		if (count > 0) this.setDraft(cellsAcross(this.draft, this.board.state.view.peek().scene.width_px, count));
	}

	get board() {
		return this.application.getControllerForElementAndIdentifier(this.element, "map-board") as MapBoard | null;
	}

	get draft() {
		return this.board.state.local.drafts.peek().grid;
	}

	setDraft(grid) {
		if (grid && this.trimmed && grid !== this.trimGrid) this.retrim(grid);
		const drafts = this.board.state.local.drafts;
		// Under a resize preview the map keeps the size the draft grid gives its saved counts: the preview draws the rest.
		const size = grid && this.counts ? sceneSize(grid, this.saved ?? this.counts) : undefined;
		const { counts, saved } = this;
		const changed = grid && counts && saved && (counts.cols !== saved.cols || counts.rows !== saved.rows);
		const resize: Resize | undefined = changed ? { from: saved, to: counts, ...this.shift } : undefined;
		drafts.value = { ...drafts.peek(), grid, size, resize };
		// What the map holds under this grid decides whether there is anything to trim.
		if (grid && counts) this.renderSides();
	}

	// The preview's words over the board: its legend, and a chip on each side that changes.
	paintPreview() {
		const { resize, grid, size } = this.board.state.local.drafts.value;
		this.legendTarget.hidden = !resize;
		for (const chip of this.chipTargets) chip.hidden = true;
		if (!resize || !grid) return;
		const { state } = this.board;
		const viewport = state.local.viewport.value;
		const { tokens, scene } = state.view.value;
		const { from, to, left, top } = resize;
		const preview = previewOf(grid as LiveGrid, size ?? scene, resize, scene);
		const pulled = preview.pulled(Object.values(tokens ?? {}) as { x: number; y: number; size: number }[]).length;
		const labels = this.labelsValue;
		this.legendSizeTarget.textContent = `${from.cols} × ${from.rows} → ${to.cols} × ${to.rows}`;
		this.legendPulledTarget.hidden = pulled === 0;
		this.legendPulledTarget.textContent = plural(labels.pulled, pulled).replace("%{count}", String(pulled));
		const corner = toScreen(viewport, preview.reach);
		const far = toScreen(viewport, { x: preview.reach.x + preview.reach.width_px, y: preview.reach.y + preview.reach.height_px });
		const middle = { x: (corner.x + far.x) / 2, y: (corner.y + far.y) / 2 };
		const changes = { left, right: to.cols - from.cols - left, top, bottom: to.rows - from.rows - top };
		for (const chip of this.chipTargets) {
			const edge = chip.dataset.edge as keyof typeof changes;
			const change = changes[edge];
			if (!change) continue;
			const horizontal = edge === "left" || edge === "right";
			const text = plural(horizontal ? labels.columns : labels.rows, change).replace("%{count}", `${change > 0 ? "+" : "−"}${Math.abs(change)}`);
			chip.textContent = { left: `← ${text}`, right: `${text} →`, top: `↑ ${text}`, bottom: `↓ ${text}` }[edge];
			chip.dataset.change = change > 0 ? "add" : "remove";
			const at = {
				left: [corner.x + 4, middle.y - 11, "0"],
				right: [far.x - 4, middle.y - 11, "-100%"],
				top: [middle.x, corner.y + 4, "-50%"],
				bottom: [middle.x, far.y - 28, "-50%"],
			}[edge];
			chip.style.left = `${at[0]}px`;
			chip.style.top = `${at[1]}px`;
			chip.style.transform = `translateX(${at[2]})`;
			chip.hidden = false;
		}
	}

	// A field being typed in keeps its text; the rest follow the draft.
	fill(grid) {
		if (!grid) return;
		for (const input of this.fieldTargets) {
			const value = grid[input.dataset.field];
			if (input instanceof HTMLInputElement && input.type === "checkbox") input.checked = value !== false;
			else if (input instanceof HTMLInputElement && input.type === "radio") input.checked = input.value === value;
			else if (input !== document.activeElement) input.value = typeof value === "number" ? String(Math.round(value * 100) / 100) : value;
		}
		for (const row of this.parityTargets) row.hidden = grid.kind !== "hex";
	}
}
